
<script setup>

import {computed, onMounted, ref, watch} from 'vue'
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/vue'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import InputLabel from "@/Components/InputLabel.vue";
import {useErrorStore} from "@/Stores/errorStore";
import debounce from "lodash/debounce";
import { nextTick } from 'vue';

const emits = defineEmits(['close', 'selected', 'selected-id']);
const errorStore = useErrorStore();
const accumulatedInput = ref('');
const renderKey = ref(0);
const inputRef = ref(null);

const props = defineProps({
    options: Object,
    label: String,
    placeholder: String,
    selectedOptionValue: {
        type: String,
        default: null
    },
    defaultOption: {
        type: Object,
        default: null
    },
});

const query = ref('')
const selectedOption = ref(null)
const filteredOptions = computed(() =>
    query.value === ''
        ? props.options
        : props.options.filter((option) => {
            return option.value.toLowerCase().includes(query.value.toLowerCase())
        })
)

const placeholder = computed(() => {
    return props.placeholder
})

const isPlaceholder = computed(() => {
    return selectedOption.value === null
})

const clearAccumulatedInput = debounce(() => {
    accumulatedInput.value = '';
}, 250);

const searchByAccumulatedInput = () => {
    const filtered = props.options.filter(option => {
        const optionValue = String(option.value).toLowerCase();
        return optionValue.startsWith(accumulatedInput.value.toLowerCase());
    });

    if (filtered.length > 0) {
        selectedOption.value = filtered[0];
    }
    clearAccumulatedInput();
};

const handleKeyDown = (event) => {
    const { key } = event;
    if (key.length === 1 && key.match(/\S/)) { // Matches any non-whitespace character
        accumulatedInput.value += key;
        searchByAccumulatedInput();
        nextTick(() => {
            inputRef.value.$el.focus();
        });
    }
};

watch(selectedOption, (newValue, oldValue) => {
    // This callback will be called whenever the value of selectedOption changes
    if (newValue == null) {
        return;
    }
    errorStore.clearError(props.label.toLowerCase())
    emits('selected', newValue.value)
    emits('selected-id', newValue.id)
    renderKey.value++;
});


watch(
    () => props.selectedOptionValue,
    async newValue => {
        if (newValue == null) {
            selectedOption.value = props.options[0];
        } else {
            selectedOption.value = props.options.find(option => option.value === newValue);
        }
    },
    { immediate: true, deep: true }
)

onMounted(() => {
    if (props.defaultOption != null) {
        selectedOption.value = props.defaultOption;
    }
});

</script>

<template>
    <Combobox as="div" v-model="selectedOption" >
        <InputLabel :id="props.label.toLowerCase().split(' ').join('_')" :for="props.label" :value="props.label" class="leading-6 text-white"/>
        <div class="relative">
            <ComboboxInput
                ref="inputRef"
                @keydown="handleKeyDown"
                :key="renderKey"
                readonly
                :class="[isPlaceholder ? 'text-gray-400' : 'text-gray-200']"
                class="w-full rounded-md bg-gray-800 pl-3 shadow-sm ring-inset ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-indigo-400 sm:text-sm sm:leading-6"
                @change="query = $event.target.value"
                :placeholder="placeholder"
                :display-value="(option) => String(option?.value ?? placeholder)"
            />
            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <font-awesome-icon class="h-3 w-3 text-gray-400" icon="fa-solid fa-chevron-down" />
            </ComboboxButton>

            <ComboboxOptions v-if="filteredOptions.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                <ComboboxOption v-for="option in filteredOptions" :key="option.id" :value="option" as="template" v-slot="{ active, selected }">
                    <li :class="['relative cursor-default select-none py-2 pl-8 pr-4', active ? 'bg-indigo-700 text-white' : 'text-gray-300']">
                        <span :class="['block truncate', selected && 'font-semibold']">
                          {{ option.value }}
                        </span>
                        <span v-if="selected" :class="['absolute inset-y-0 left-1 flex items-center pl-1.5', active ? 'text-white' : 'text-indigo-400']">
                            <FontAwesomeIcon class="h-3 w-3" icon="fa-solid fa-check" />
                        </span>
                        <span v-else-if="option.icon" :class="['absolute inset-y-0 left-1 flex items-center pl-1.5 text-gray-300']">
                            <FontAwesomeIcon class="h-3 w-3" :icon="option.icon" />
                        </span>
                    </li>
                </ComboboxOption>
            </ComboboxOptions>
        </div>
    </Combobox>
</template>


